/*
 * SHIN auth UI — registration, login, forgot-password and the account area, against the
 * CtrlDigit middleware (API_CONTRACT_AUTH.md).
 *
 * There is deliberately NO handoff to Shopify's /account/login. That route 302s to
 * Shopify's hosted, passwordless customer accounts and cannot mint a session for us; the
 * password of record and the storefront session both live in the middleware. Signing in
 * therefore means: middleware verifies password -> emails an OTP -> sets an HttpOnly
 * session cookie. Page-level state comes from assets/sumitomo-session.js.
 *
 * Every request sends credentials: without them the cookie is invisible and each call
 * looks signed-out.
 */

const SumitomoAuth = {
  base(el) {
    // window.SUMITOMO_API_BASE wins: it is the only value that knows which HOST the page
    // is being served from (theme.liquid decides it in the browser). The section's
    // data-api-base is a Liquid setting, so on a theme-dev page it still points at
    // production — using it there makes every call a blocked cross-origin request.
    return (window.SUMITOMO_API_BASE || el.dataset.apiBase || '').replace(/\/$/, '');
  },

  /**
   * Sign-in and register are for signed-out visitors only. A buyer who already has a
   * session — arriving by link, typed address or the browser's Back button — is sent to
   * My Account. `replace` keeps this page out of the history, so Back does not loop.
   */
  leaveIfSignedIn(el) {
    const go = (tier) => {
      if (tier && tier !== 'guest') window.location.replace(el.dataset.redirect || '/pages/account');
    };
    const session = window.SumitomoSession;
    if (session && session.loaded) go(session.tier);
    document.addEventListener('sumitomo:session', (e) => go(e.detail.tier));
    // Back/Forward can restore this page from the browser's cache without running any
    // script, showing the form as it was before signing in. Reload so the session is
    // checked again.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) window.location.reload();
    });
  },

  async api(base, path, body, method = 'POST') {
    let res;
    try {
      res = await fetch(base + path, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: method === 'GET' ? undefined : JSON.stringify(body || {}),
      });
    } catch (e) {
      return { ok: false, status: 0, data: { error: 'network' } };
    }
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      /* non-JSON error body */
    }
    return { ok: res.ok, status: res.status, data };
  },

  /** Same classification as the account area: a failed request must say which of the four
   *  things went wrong, because the fix differs — reconnect, wait, sign in again, or
   *  nothing the buyer can do. `el` supplies the copy through data-* attributes. */
  failureMessage(res, el) {
    const d = el.dataset;
    if (!res || res.status === 0) return d.errOffline || d.errNetwork;
    if (res.status === 429) {
      const wait = res.data?.retryAfterSec;
      return (d.errRate || '').replace('{seconds}', wait || 60);
    }
    if (res.status >= 500) {
      const ref = res.data?.reference;
      const base = d.errServer || d.errNetwork;
      return ref && d.errReference ? `${base} ${d.errReference.replace('{id}', ref)}` : base;
    }
    if (Array.isArray(res.data?.errors) && res.data.errors.length) return res.data.errors.join(' · ');
    return d.errNetwork;
  },

  validators: {
    email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    // Strip the country code only when it IS one. `.replace(/^\+?91/, '')` also ate the
    // first two digits of any real mobile beginning 91 — 9167041276 became 67041276 and
    // was rejected. Mirrors normalizeIndianMobile() in the middleware.
    phone: (v) => {
      const digits = String(v).replace(/\D/g, '');
      const national =
        digits.length === 12 && digits.startsWith('91') ? digits.slice(2)
        : digits.length === 11 && digits.startsWith('0') ? digits.slice(1)
        : digits;
      return /^[6-9]\d{9}$/.test(national);
    },
    pin: (v) => /^\d{6}$/.test(v),
    password: (v) => v.length >= 15 && /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v),
    gstin: (v) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(v),

    /* The PIN's postal circle against the state chosen (SHIN feedback 31 Aug, slide 17).
       A mismatch used to be accepted here and refused by Shopify at checkout, where the
       buyer no longer connected it to what they had typed.

       Mirrors src/pin-code.ts in the middleware, which is the enforcing copy and the
       source of truth — this one only lets the message appear under the field as the
       buyer leaves it. Same rule: only says no when it is certain, because several
       circles genuinely span two states. */
    pinState: (zip, provinceCode) => {
      if (!/^[1-9][0-9]{5}$/.test(String(zip || ''))) return true;
      const code = String(provinceCode || '').toUpperCase();
      if (!code) return true;
      const circles = {
        11: 'DL', 12: 'HR', 13: 'HR', 14: 'PB', 15: 'PB', 16: 'PB CH', 17: 'HP',
        18: 'JK LA', 19: 'JK LA',
        20: 'UP UK', 21: 'UP UK', 22: 'UP UK', 23: 'UP UK', 24: 'UP UK', 25: 'UP UK',
        26: 'UP UK', 27: 'UP UK', 28: 'UP UK',
        30: 'RJ', 31: 'RJ', 32: 'RJ', 33: 'RJ', 34: 'RJ',
        36: 'GJ DN DD', 37: 'GJ DN DD', 38: 'GJ DN DD', 39: 'GJ DN DD',
        40: 'MH GA', 41: 'MH GA', 42: 'MH GA', 43: 'MH GA', 44: 'MH GA',
        45: 'MP', 46: 'MP', 47: 'MP', 48: 'MP', 49: 'CG',
        50: 'TS AP', 51: 'TS AP', 52: 'TS AP', 53: 'TS AP',
        56: 'KA', 57: 'KA', 58: 'KA', 59: 'KA',
        60: 'TN PY', 61: 'TN PY', 62: 'TN PY', 63: 'TN PY', 64: 'TN PY', 65: 'TN PY',
        66: 'TN PY', 67: 'KL LD', 68: 'KL LD', 69: 'KL LD',
        70: 'WB SK AN', 71: 'WB SK AN', 72: 'WB SK AN', 73: 'WB SK AN', 74: 'WB SK AN',
        75: 'OR', 76: 'OR', 77: 'OR', 78: 'AS', 79: 'AR AS ML MN MZ NL TR',
        80: 'BR JH', 81: 'BR JH', 82: 'BR JH', 83: 'BR JH', 84: 'BR JH', 85: 'BR JH',
      };
      const allowed = circles[String(zip).slice(0, 2)];
      // An unknown circle is unknown, never wrong: the server has the final say.
      return !allowed || allowed.split(' ').includes(code);
    },
  },

  passwordStrength(v) {
    let score = 0;
    if (v.length >= 15) score++;
    if (v.length >= 20) score++;
    if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score++;
    if (/\d/.test(v)) score++;
    if (/[^A-Za-z0-9]/.test(v)) score++;
    return Math.min(score, 4);
  },

  startCountdown(el, seconds, doneText) {
    let left = seconds;
    el.hidden = false;
    const tick = () => {
      el.textContent = el.dataset.template.replace('SECONDS', left);
      if (left-- <= 0) {
        clearInterval(timer);
        el.textContent = doneText || '';
      }
    };
    const timer = setInterval(tick, 1000);
    tick();
    return timer;
  },
};

class SumitomoOtpPanel {
  /* Wires an OTP entry panel (snippet sumitomo-otp-panel).
     onVerify(code) -> Promise<{ok, status, data}> */
  constructor(root, { onVerify, onResend }) {
    this.root = root;
    this.input = root.querySelector('[data-otp-input]');
    this.error = root.querySelector('[data-otp-error]');
    this.resend = root.querySelector('[data-otp-resend]');
    this.countdownEl = root.querySelector('[data-otp-countdown]');
    this.submitBtn = root.querySelector('[data-otp-submit]');
    this.onVerify = onVerify;

    this.submitBtn?.addEventListener('click', () => this.verify());
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.verify();
    });
    this.resend?.addEventListener('click', async () => {
      this.resend.disabled = true;
      const res = await onResend?.();
      // SHIN feedback 31 Aug (slide 15): the countdown kept running down from the FIRST
      // code, so a buyer who resent watched it hit zero and read "expired" while holding
      // a code that was good for another ten minutes. A resend restarts the clock, clears
      // the stale error and empties the box that still held the old code.
      //
      // Only on a code that was actually sent. If the resend was refused (rate limit),
      // the previous code is still the live one, so its clock must keep running.
      if (res && res.ok) this.reset(res.data?.expiresInSec);
      else if (res) this.setError(SumitomoAuth.failureMessage(res, this.root));
      setTimeout(() => (this.resend.disabled = false), 30000);
    });
  }

  show(expiresInSec) {
    this.root.hidden = false;
    this.reset(expiresInSec);
    this.input.focus();
  }

  /** Back to "a fresh code was just sent": empty box, no error, clock restarted. */
  reset(expiresInSec) {
    this.input.value = '';
    this.setError('');
    if (!this.countdownEl) return;
    // One timer at a time — two running together tick the same element and the seconds
    // jump around.
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    if (expiresInSec) {
      this.countdownTimer = SumitomoAuth.startCountdown(this.countdownEl, expiresInSec, this.countdownEl.dataset.expired);
    } else {
      // No expiry to show beats a frozen number left over from the previous code.
      this.countdownEl.hidden = true;
      this.countdownEl.textContent = '';
    }
  }

  hide() {
    this.root.hidden = true;
  }

  setError(msg) {
    this.error.textContent = msg;
    this.error.hidden = !msg;
  }

  async verify() {
    const code = this.input.value.trim();
    if (!/^\d{6}$/.test(code)) {
      this.setError(this.root.dataset.errFormat);
      return;
    }
    this.submitBtn.disabled = true;
    const res = await this.onVerify(code);
    this.submitBtn.disabled = false;
    if (res && !res.ok) {
      const reason = res.data.reason || res.data.error || 'unknown';
      const msgs = {
        wrong_code: this.root.dataset.errWrong,
        expired: this.root.dataset.errExpired,
        too_many_attempts: this.root.dataset.errAttempts,
        no_code: this.root.dataset.errExpired,
        network: this.root.dataset.errNetwork,
      };
      this.setError(msgs[reason] || this.root.dataset.errWrong);
    }
    return res;
  }
}

/* ---------------------------------------------------------------- login + forgot */

if (!customElements.get('sumitomo-login')) {
  customElements.define(
    'sumitomo-login',
    class extends HTMLElement {
      connectedCallback() {
        this.base = SumitomoAuth.base(this);
        SumitomoAuth.leaveIfSignedIn(this);
        this.form = this.querySelector('[data-login-form]');
        this.formError = this.querySelector('[data-login-error]');

        this.otp = new SumitomoOtpPanel(this.querySelector('[data-otp-panel]'), {
          onVerify: async (code) => {
            const res = await SumitomoAuth.api(this.base, '/login/verify', { email: this.email, code });
            // The cookie arrives on this response — from here the buyer is signed in.
            if (res.ok && res.data.ok) this.finish();
            return res;
          },
          onResend: () => SumitomoAuth.api(this.base, '/login/start', { email: this.email, password: this.password }),
        });

        this.querySelector('[data-login-submit]')?.addEventListener('click', () => this.start());
        this.form?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.start();
          }
        });

        this.querySelector('[data-forgot-link]')?.addEventListener('click', (e) => {
          e.preventDefault();
          this.querySelector('[data-forgot-panel]').hidden = false;
          this.form.hidden = true;
        });
        this.querySelector('[data-forgot-back]')?.addEventListener('click', (e) => {
          e.preventDefault();
          this.resetForgot();
        });
        this.querySelector('[data-forgot-submit]')?.addEventListener('click', () => this.forgot());
        this.querySelector('[data-reset-submit]')?.addEventListener('click', () => this.reset());

        this.querySelectorAll('[data-pwd-toggle]').forEach((btn) =>
          btn.addEventListener('click', () => {
            const input = this.querySelector(btn.dataset.pwdToggle);
            input.type = input.type === 'password' ? 'text' : 'password';
          }),
        );
      }

      setError(msg) {
        this.formError.textContent = msg;
        this.formError.hidden = !msg;
      }

      finish() {
        // replace, not href: the sign-in page must not sit behind My Account in the history.
        window.location.replace(this.dataset.redirect || '/');
      }

      async start() {
        this.email = this.querySelector('[data-login-email]').value.trim();
        this.password = this.querySelector('[data-login-password]').value;
        if (!SumitomoAuth.validators.email(this.email) || !this.password) {
          this.setError(this.dataset.errCredentials);
          return;
        }
        this.setError('');
        const res = await SumitomoAuth.api(this.base, '/login/start', { email: this.email, password: this.password });
        // The payment-gateway reviewer account is signed in at this step (no code emailed).
        if (res.ok && res.data.ok && !res.data.otpSent) {
          this.finish();
          return;
        }
        if (res.ok && res.data.otpSent) {
          this.form.hidden = true;
          this.otp.show(res.data.expiresInSec);
        } else if (res.status === 401) {
          this.setError(this.dataset.errCredentials);
        } else {
          this.setError(SumitomoAuth.failureMessage(res, this));
        }
      }

      resetForgot() {
        this.querySelector('[data-forgot-panel]').hidden = true;
        this.querySelector('[data-forgot-fields]').hidden = false;
        this.querySelector('[data-reset-fields]').hidden = true;
        const done = this.querySelector('[data-forgot-done]');
        if (done) done.hidden = true;
        this.form.hidden = false;
      }

      /* Step 1: ask for a reset code. The response is identical for unknown addresses,
         so the UI must not imply that the account exists. */
      async forgot() {
        const email = this.querySelector('[data-forgot-email]').value.trim();
        const errorEl = this.querySelector('[data-forgot-error]');
        if (!SumitomoAuth.validators.email(email)) {
          if (errorEl) {
            errorEl.textContent = this.dataset.errCredentials;
            errorEl.hidden = false;
          }
          return;
        }
        this.resetEmail = email;
        await SumitomoAuth.api(this.base, '/password/forgot', { email });
        if (errorEl) errorEl.hidden = true;
        this.querySelector('[data-forgot-fields]').hidden = true;
        this.querySelector('[data-reset-fields]').hidden = false;
        const done = this.querySelector('[data-forgot-done]');
        if (done) done.hidden = false;
      }

      /* Step 2: code + new password. Every existing session is revoked server-side, so
         the buyer is sent back to sign in with the new password. */
      async reset() {
        const code = this.querySelector('[data-reset-code]').value.trim();
        const password = this.querySelector('[data-reset-password]').value;
        const errorEl = this.querySelector('[data-reset-error]');
        const fail = (msg) => {
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.hidden = false;
          }
        };

        if (!/^\d{6}$/.test(code)) return fail(this.dataset.errOtpFormat);
        if (!SumitomoAuth.validators.password(password)) return fail(this.dataset.errPassword);

        const res = await SumitomoAuth.api(this.base, '/password/reset', { email: this.resetEmail, code, password });
        if (res.ok && res.data.reset) {
          this.resetForgot();
          this.setError('');
          const notice = this.querySelector('[data-reset-success]');
          if (notice) notice.hidden = false;
          return;
        }
        if (res.status === 400 && res.data.errors) return fail(res.data.errors.join(' · '));
        if (res.status === 400) return fail(this.dataset.errOtpWrong);
        fail(SumitomoAuth.failureMessage(res, this));
      }
    },
  );
}

/* ------------------------------------------------------------------- registration */

if (!customElements.get('sumitomo-register')) {
  customElements.define(
    'sumitomo-register',
    class extends HTMLElement {
      connectedCallback() {
        this.base = SumitomoAuth.base(this);
        SumitomoAuth.leaveIfSignedIn(this);
        this.step = 1;
        this.steps = [...this.querySelectorAll('[data-reg-step]')];
        this.dots = [...this.querySelectorAll('[data-step-dot]')];
        this.stepLabel = this.querySelector('[data-step-label]');
        this.formError = this.querySelector('[data-reg-error]');
        this.backBtn = this.querySelector('[data-reg-back]');
        this.nextBtn = this.querySelector('[data-reg-next]');

        this.otp = new SumitomoOtpPanel(this.querySelector('[data-otp-panel]'), {
          onVerify: async (code) => {
            const res = await SumitomoAuth.api(this.base, '/register/confirm', { email: this.payload.email, code });
            if (res.ok && res.data.registered) {
              // Confirming also signs the buyer in (session cookie on this response),
              // so there is no second login step.
              this.otp.hide();
              const success = this.querySelector('[data-reg-success]');
              if (success) success.hidden = false;
              setTimeout(() => {
                window.location.replace(this.dataset.redirect || '/');
              }, 1200);
              return res;
            }
            // A duplicate phone/email only surfaces here, once the customer is created.
            if (res.status === 409) {
              this.otp.hide();
              this.showConflict(res.data);
            }
            return res;
          },
          onResend: () => SumitomoAuth.api(this.base, '/register', this.payload),
        });

        this.backBtn?.addEventListener('click', () => this.goto(this.step - 1));
        this.nextBtn?.addEventListener('click', () => this.next());
        this.querySelectorAll('[data-pwd-toggle]').forEach((btn) =>
          btn.addEventListener('click', () => {
            const input = this.querySelector(btn.dataset.pwdToggle);
            input.type = input.type === 'password' ? 'text' : 'password';
          }),
        );

        const pwd = this.querySelector('[data-reg-password]');
        pwd?.addEventListener('input', () => this.renderStrength(pwd.value));
        this.querySelector('[data-reg-gstin]')?.addEventListener('blur', () => this.checkGstin());

        // Ask whether the email / phone is already registered as the buyer leaves the
        // field. Shopify enforces one phone per customer and we cannot know that locally,
        // so without this the clash only appeared at the end, after the OTP was spent.
        this.taken = { email: false, phone: false };
        ['email', 'phone'].forEach((name) => {
          this.field(name)?.addEventListener('blur', () => this.checkAvailability(name));
          this.field(name)?.addEventListener('input', () => this.clearFieldError(name));
        });

        this.goto(1);
      }

      field(name) {
        return this.querySelector(`[data-reg-field="${name}"]`);
      }
      value(name) {
        return (this.field(name)?.value || '').trim();
      }

      setError(msg) {
        this.formError.textContent = msg;
        this.formError.hidden = !msg;
      }

      /* 409 = the email or phone is already registered. Put the message on the offending
         field and send the buyer to the step that owns it, rather than a generic error. */
      showConflict(data) {
        const stepOf = { email: 1, phone: 1 };
        const target = data.field && stepOf[data.field] ? stepOf[data.field] : 1;
        this.querySelector('[data-reg-form]').hidden = false;
        this.goto(target);
        this.setError(data.message || this.dataset.errNetwork);
        const template = data.field === 'phone' ? this.dataset.errPhoneTaken : this.dataset.errEmailTaken;
        this.setFieldError(data.field, (template || '').replace('{link}', this.dataset.signInUrl || '/pages/sign-in'));
        this.field(data.field)?.focus();
      }

      renderStrength(v) {
        const fill = this.querySelector('[data-strength-fill]');
        const label = this.querySelector('[data-strength-label]');
        const score = SumitomoAuth.passwordStrength(v);
        const widths = ['5%', '25%', '50%', '75%', '100%'];
        const colors = ['#ba1a1a', '#ba1a1a', '#e8a13c', '#45b035', '#45b035'];
        const labels = JSON.parse(this.dataset.strengthLabels);
        fill.style.width = v ? widths[score] : '0%';
        fill.style.background = colors[score];
        label.textContent = v ? labels[score] : labels[0];
      }

      async checkGstin() {
        const v = this.value('gstin').toUpperCase();
        const hint = this.querySelector('[data-gstin-hint]');
        if (v.length !== 15) return;
        if (!SumitomoAuth.validators.gstin(v)) {
          hint.textContent = hint.dataset.invalid;
          return;
        }
        const res = await SumitomoAuth.api(this.base, '/gstin/validate', { gstin: v });
        if (res.status === 0) return; // middleware unreachable: local format check stands
        hint.textContent = res.data.valid ? hint.dataset.valid : hint.dataset.invalid;
      }

      /** Inline, field-level message — the 409 belongs next to the input that caused it. */
      setFieldError(name, message) {
        const input = this.field(name);
        if (!input) return;
        input.classList.toggle('field-error', Boolean(message));
        const wrap = input.closest('.field-wrap') || input.parentElement;
        let hint = wrap?.querySelector('[data-field-error]');
        if (!hint && message && wrap) {
          hint = document.createElement('p');
          hint.className = 'hint field-error-text';
          hint.setAttribute('data-field-error', '');
          wrap.appendChild(hint);
        }
        if (hint) {
          hint.innerHTML = message || '';
          hint.hidden = !message;
        }
      }

      clearFieldError(name) {
        this.taken[name] = false;
        this.setFieldError(name, '');
      }

      async checkAvailability(name) {
        const value = this.value(name);
        const valid = name === 'email' ? SumitomoAuth.validators.email(value) : SumitomoAuth.validators.phone(value);
        if (!value || !valid) return;

        const res = await SumitomoAuth.api(this.base, '/register/check', { [name]: value });
        // The check is advisory: if it cannot run, registration still proceeds and the
        // server enforces uniqueness — never block a buyer because a lookup failed.
        if (!res.ok || res.data?.unavailable) return;

        if (res.data[name] === 'taken') {
          this.taken[name] = true;
          const link = this.dataset.signInUrl || '/pages/sign-in';
          const template = name === 'email' ? this.dataset.errEmailTaken : this.dataset.errPhoneTaken;
          this.setFieldError(name, (template || '').replace('{link}', link));
        } else {
          this.clearFieldError(name);
        }
      }

      /**
       * Every problem on the step, each against its own field.
       *
       * SHIN feedback 31 Aug (slide 4): one message at the top of the form told the buyer
       * something was wrong but not which box, and with several bad fields they fixed them
       * one round trip at a time. Errors now render under the field they belong to and all
       * of them appear at once. The returned string is only used for the summary line.
       */
      validateStep(n) {
        const v = SumitomoAuth.validators;
        const problems = [];
        const fail = (field, message) => problems.push({ field, message });

        if (n === 1) {
          if (!this.value('firstName')) fail('firstName', this.dataset.errName);
          if (!this.value('lastName')) fail('lastName', this.dataset.errName);
          if (!v.email(this.value('email'))) fail('email', this.dataset.errEmail);
          if (!v.phone(this.value('phone'))) fail('phone', this.dataset.errPhone);
          if (!v.password(this.value('password'))) fail('password', this.dataset.errPassword);
          // Confirm password: only complain once they have typed something, and only about
          // the mismatch — the rules themselves belong on the password field above.
          const confirm = this.value('passwordConfirm');
          if (this.field('passwordConfirm') && confirm && confirm !== this.value('password')) {
            fail('passwordConfirm', this.dataset.errPasswordMatch);
          }
          if (this.field('passwordConfirm') && !confirm && this.value('password')) {
            fail('passwordConfirm', this.dataset.errPasswordConfirm);
          }
          if (this.taken?.email) fail('email', this.dataset.errEmailTakenShort);
          if (this.taken?.phone) fail('phone', this.dataset.errPhoneTakenShort);
        }
        if (n === 2) {
          if (!this.value('companyName')) fail('companyName', this.dataset.errCompany);
          const g = this.value('gstin').toUpperCase();
          if (!g || !v.gstin(g)) fail('gstin', this.dataset.errGstin);
          if (!this.value('industry')) fail('industry', this.dataset.errIndustry);
        }
        if (n === 3) {
          if (!this.value('address1')) fail('address1', this.dataset.errAddress);
          if (!this.value('city')) fail('city', this.dataset.errAddress);
          if (!v.pin(this.value('zip'))) fail('zip', this.dataset.errPin);
          if (!this.value('province')) fail('province', this.dataset.errAddress);
          // PIN against the state, before Shopify refuses the address at checkout
          // (SHIN feedback 31 Aug, slide 17). Named in the message with the state as the
          // buyer sees it in the list, not its two-letter code.
          if (v.pin(this.value('zip')) && this.value('province') && !v.pinState(this.value('zip'), this.value('province'))) {
            const select = this.field('province');
            const stateName = select?.options?.[select.selectedIndex]?.text || this.value('province');
            fail('zip', (this.dataset.errPinState || '').replace('{state}', stateName));
          }
        }

        // Clear every field on this step first, so a fixed field stops showing its error.
        this.fieldsOnStep(n).forEach((name) => this.clearFieldError(name));
        problems.forEach((p) => this.setFieldError(p.field, p.message));

        // The terms checkbox has no field-wrap to hang a message under, so it stays a
        // form-level message.
        const termsMissing = n === 3 && !this.querySelector('[data-reg-terms]').checked;
        return {
          valid: problems.length === 0 && !termsMissing,
          message: termsMissing ? this.dataset.errTerms : '',
        };
      }

      /** Field names per step, so validateStep can clear stale errors before re-checking. */
      fieldsOnStep(n) {
        if (n === 1) return ['firstName', 'lastName', 'email', 'phone', 'password', 'passwordConfirm'];
        if (n === 2) return ['companyName', 'gstin', 'industry'];
        return ['address1', 'city', 'zip', 'province'];
      }

      goto(n) {
        this.step = Math.max(1, Math.min(3, n));
        this.steps.forEach((el, i) => (el.hidden = i !== this.step - 1));
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === this.step - 1);
          dot.classList.toggle('done', i < this.step - 1);
        });
        const labels = JSON.parse(this.dataset.stepLabels);
        this.stepLabel.textContent = labels[this.step - 1];
        this.backBtn.hidden = this.step === 1;
        this.nextBtn.textContent = this.step === 3 ? this.dataset.labelSubmit : this.dataset.labelContinue;
        this.setError('');
      }

      async next() {
        // Field-level problems are already shown under their own inputs, so the summary
        // line is only used for anything that has nowhere else to appear.
        const check = this.validateStep(this.step);
        if (!check.valid) {
          this.setError(check.message);
          return;
        }
        this.setError('');
        if (this.step < 3) {
          this.goto(this.step + 1);
          return;
        }

        this.payload = {
          companyName: this.value('companyName'),
          firstName: this.value('firstName'),
          lastName: this.value('lastName'),
          email: this.value('email'),
          gstin: this.value('gstin').toUpperCase(),
          industry: this.value('industry'),
          address1: this.value('address1'),
          address2: this.value('address2') || '',
          city: this.value('city'),
          province: this.value('province'),
          zip: this.value('zip'),
          phone: this.value('phone').replace(/[\s-]/g, ''),
          password: this.value('password'),
        };

        this.nextBtn.disabled = true;
        const res = await SumitomoAuth.api(this.base, '/register', this.payload);
        this.nextBtn.disabled = false;

        if (res.ok && res.data.otpSent) {
          this.querySelector('[data-reg-form]').hidden = true;
          this.otp.show(res.data.expiresInSec);
        } else if (res.status === 409) {
          this.showConflict(res.data);
        } else {
          this.setError(SumitomoAuth.failureMessage(res, this));
        }
      }
    },
  );
}

/* The account area lives in assets/sumitomo-account.js — it outgrew sharing a file
 * with the login and register elements. */
